import React, { useEffect, useRef, useState } from 'react'
import {
  Heading,
  Text,
  Button,
  Input,
  LoadingSpinner,
} from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { iaDocumentoService } from '../../services/iaDocumento'
import type { IaDocumentoChat, IaDocumentoMessage } from '../../types/iaDocumento'
import { BRAND_COLOR } from '../../constants'
import {
  PlusIcon,
  SparklesIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  PaperAirplaneIcon,
  DocumentArrowUpIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const MODULE_NAME = 'ia_documento'
const LOG_ORIGEM = 'ia/ia-documento'
const LOG_ENTITY_TYPE = 'ia_documento' as const

const IADocumento: React.FC = () => {
  const { user, profile } = useAuthContext()
  const { logAction } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('IA Documento')
  const [moduleDescription, setModuleDescription] = useState(
    'Envie um documento e converse com a Tess sobre o conteúdo'
  )

  const [chats, setChats] = useState<IaDocumentoChat[]>([])
  const [currentChat, setCurrentChat] = useState<IaDocumentoChat | null>(null)
  const [messages, setMessages] = useState<IaDocumentoMessage[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [composingNew, setComposingNew] = useState(false)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [webhookOffline, setWebhookOffline] = useState(false)

  const [editingChatId, setEditingChatId] = useState<string | null>(null)
  const [editingChatName, setEditingChatName] = useState('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const getFirstName = (): string => {
    if (profile?.name?.trim()) {
      return profile.name.trim().split(/\s+/)[0]
    }
    if (user?.email) {
      return user.email.split('@')[0]
    }
    return 'Usuário'
  }

  const displayUserMessage = (raw: string): string => {
    const prefix = `${getFirstName()}: `
    return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw
  }

  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName(MODULE_NAME)
        if (result.success && result.data) {
          if (result.data.display_name) setModuleTitle(result.data.display_name)
          if (result.data.description) setModuleDescription(result.data.description)
        }
      } catch (err) {
        console.error('Erro ao buscar informações do módulo:', err)
      }
    }
    void loadModuleInfo()
  }, [])

  const loadChats = async (selectId?: string) => {
    if (!user) return
    const data = await iaDocumentoService.listChats(user.id)
    setChats(data)
    if (selectId) {
      const found = data.find((c) => c.id === selectId) ?? null
      setCurrentChat(found)
      setComposingNew(false)
    } else if (data.length > 0 && !composingNew) {
      setCurrentChat((prev) => {
        if (prev && data.some((c) => c.id === prev.id)) {
          return data.find((c) => c.id === prev.id) ?? data[0]
        }
        return data[0]
      })
    }
  }

  useEffect(() => {
    const init = async () => {
      if (!user) return
      try {
        setLoading(true)
        setError(null)
        await loadChats()
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar conversas')
      } finally {
        setLoading(false)
      }
    }
    void init()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once per user
  }, [user?.id])

  useEffect(() => {
    const fetchMessages = async () => {
      if (!currentChat) {
        setMessages([])
        return
      }
      try {
        setMessagesLoading(true)
        const data = await iaDocumentoService.listMessages(currentChat.id)
        setMessages(data)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar mensagens')
      } finally {
        setMessagesLoading(false)
      }
    }
    void fetchMessages()
  }, [currentChat?.id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending])

  const startNewConversation = () => {
    setComposingNew(true)
    setCurrentChat(null)
    setMessages([])
    setSelectedFile(null)
    setUploadError(null)
    setInputMessage('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setSelectedFile(file ?? null)
    setUploadError(null)
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setUploadError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleUploadDocumento = async () => {
    if (!user || !selectedFile) {
      setUploadError('Selecione um arquivo para enviar.')
      return
    }

    setUploading(true)
    setUploadError(null)
    setError(null)

    try {
      const { chat, documentId, webhookOk } = await iaDocumentoService.uploadDocumento({
        file: selectedFile,
        profileId: user.id,
      })

      setWebhookOffline(!webhookOk)

      void logAction(
        'import',
        LOG_ENTITY_TYPE,
        documentId ?? chat.id,
        `Enviou documento para Tess: ${selectedFile.name}`,
        {
          origem: LOG_ORIGEM,
          file_name: selectedFile.name,
          chat_id: chat.id,
          document_id: documentId,
          webhook_ok: webhookOk,
        }
      )

      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setComposingNew(false)
      await loadChats(chat.id)
      setTimeout(() => inputRef.current?.focus(), 100)
    } catch (err: unknown) {
      setUploadError(
        err instanceof Error ? err.message : 'Erro ao enviar documento. Tente novamente.'
      )
    } finally {
      setUploading(false)
    }
  }

  const sendMessage = async () => {
    if (!user || !currentChat || !inputMessage.trim()) return
    if (!currentChat.document_id && !currentChat.file_name) return

    const messageText = inputMessage.trim()
    const normalized = messageText
      .replace(/\s*\n+\s*/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .replace(/"/g, "'")
      .trim()
    const messageForApi = `${getFirstName()}: ${normalized}`

    setInputMessage('')
    setSending(true)
    setError(null)

    const optimisticId = `optimistic-${Date.now()}`
    const optimisticMessage: IaDocumentoMessage = {
      id: optimisticId,
      chat_id: currentChat.id,
      user_message: messageForApi,
      ai_message: null,
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, optimisticMessage])

    try {
      const { messages: updated, webhookOk } = await iaDocumentoService.enviarPergunta({
        profileId: user.id,
        chatId: currentChat.id,
        documentId: currentChat.document_id ?? currentChat.id,
        tessRootId: currentChat.tess_root_id ?? null,
        message: messageForApi,
      })

      if (!webhookOk) setWebhookOffline(true)
      setMessages(updated)

      void logAction(
        'other',
        LOG_ENTITY_TYPE,
        currentChat.document_id ?? currentChat.id,
        'Perguntou à Tess sobre documento',
        {
          origem: LOG_ORIGEM,
          chat_id: currentChat.id,
          pergunta: normalized.slice(0, 200),
          webhook_ok: webhookOk,
        }
      )
    } catch (err: unknown) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
      setError(err instanceof Error ? err.message : 'Erro ao enviar mensagem')
      setInputMessage(messageText)
    } finally {
      setSending(false)
    }
  }

  const startEditingChat = (chat: IaDocumentoChat) => {
    setEditingChatId(chat.id)
    setEditingChatName(chat.subject)
  }

  const cancelEditingChat = () => {
    setEditingChatId(null)
    setEditingChatName('')
  }

  const saveChatName = async (chatId: string) => {
    if (!editingChatName.trim()) return
    try {
      await iaDocumentoService.renameChat(chatId, editingChatName.trim())
      setChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, subject: editingChatName.trim() } : c))
      )
      if (currentChat?.id === chatId) {
        setCurrentChat((prev) => (prev ? { ...prev, subject: editingChatName.trim() } : null))
      }
      cancelEditingChat()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao renomear chat')
    }
  }

  const deleteChat = async (chatId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este chat?')) return
    try {
      await iaDocumentoService.softDeleteChat(chatId)
      setChats((prev) => prev.filter((c) => c.id !== chatId))
      if (currentChat?.id === chatId) {
        setCurrentChat(null)
        setMessages([])
        setComposingNew(true)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir chat')
    }
  }

  const retryLoad = async () => {
    if (!user) return
    setError(null)
    setLoading(true)
    try {
      await loadChats()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar conversas')
    } finally {
      setLoading(false)
    }
  }

  const showUploadPanel = composingNew || (!currentChat && chats.length === 0)
  const canChat = !!(currentChat?.document_id || currentChat?.file_name)

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <Text size="lg" className="text-gray-600 dark:text-gray-400">
            Carregando conversas...
          </Text>
        </div>
      </div>
    )
  }

  if (error && chats.length === 0 && !composingNew) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <Text size="lg" className="text-gray-900 dark:text-white mb-2">
            Erro ao carregar
          </Text>
          <Text size="sm" color="muted" className="mb-4">
            {error}
          </Text>
          <Text size="xs" color="muted" className="mb-4">
            Se as tabelas ainda não existem, execute o script{' '}
            <code className="text-xs">frontend/sqls/ia_08_ai_document_chat_tables.sql</code> no
            Supabase SQL Editor.
          </Text>
          <Button
            onClick={() => void retryLoad()}
            className="!text-white dark:!text-white"
            style={{ backgroundColor: BRAND_COLOR }}
          >
            <ArrowPathIcon className="h-4 w-4 mr-2 inline" />
            Tentar novamente
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
              <Heading as="h1" size="2xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text size="sm" color="muted" className="mt-1">
                {moduleDescription}
              </Text>
            </div>

            <div className="flex-1 flex overflow-hidden bg-gray-50 dark:bg-gray-900">
              {/* Sidebar de chats */}
              <div className="w-52 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <Button
                    onClick={startNewConversation}
                    disabled={uploading}
                    className="w-full flex items-center justify-center gap-2 !text-white dark:!text-white"
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    <PlusIcon className="h-5 w-5" />
                    Nova Conversa
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {chats.map((chat) => (
                    <div
                      key={chat.id}
                      className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                        currentChat?.id === chat.id && !composingNew
                          ? 'bg-gray-100 dark:bg-gray-700'
                          : ''
                      }`}
                      onClick={() => {
                        if (!editingChatId) {
                          setComposingNew(false)
                          setCurrentChat(chat)
                        }
                      }}
                    >
                      {editingChatId === chat.id ? (
                        <div
                          className="flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Input
                            value={editingChatName}
                            onChange={(e) => setEditingChatName(e.target.value)}
                            className="flex-1 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveChatName(chat.id)
                              if (e.key === 'Escape') cancelEditingChat()
                            }}
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => void saveChatName(chat.id)}
                            className="p-1 text-green-600 hover:text-green-700"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditingChat}
                            className="p-1 text-red-600 hover:text-red-700"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <Text
                              size="sm"
                              className="font-medium text-gray-900 dark:text-white truncate text-left"
                            >
                              {chat.subject}
                            </Text>
                            <Text size="xs" className="text-gray-500 dark:text-gray-400 text-left">
                              {new Date(chat.created_at).toLocaleDateString('pt-BR')}
                            </Text>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                startEditingChat(chat)
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                void deleteChat(chat.id)
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Área principal */}
              <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 min-w-0">
                {showUploadPanel ? (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <div className="w-full max-w-lg space-y-6">
                      <div className="text-center">
                        <div
                          className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: BRAND_COLOR }}
                        >
                          <DocumentArrowUpIcon className="h-8 w-8 text-white" />
                        </div>
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
                          Enviar documento
                        </Heading>
                        <Text size="sm" color="muted">
                          Envie um arquivo para a Tess ler. Depois você poderá fazer perguntas
                          sobre o conteúdo.
                        </Text>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Arquivo
                          </Text>
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileSelect}
                            className="block w-full text-sm text-gray-700 dark:text-gray-300
                              file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                              file:text-sm file:font-medium file:text-white
                              file:bg-[var(--brand)] file:cursor-pointer
                              cursor-pointer"
                            style={
                              {
                                '--brand': BRAND_COLOR,
                              } as React.CSSProperties
                            }
                            disabled={uploading}
                          />
                        </div>

                        {selectedFile && (
                          <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                            <div className="min-w-0">
                              <Text
                                size="sm"
                                className="text-gray-900 dark:text-white truncate"
                              >
                                {selectedFile.name}
                              </Text>
                              <Text size="xs" color="muted">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              </Text>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveFile}
                              className="text-red-500 hover:text-red-700 ml-2"
                              disabled={uploading}
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )}

                        {uploadError && (
                          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                            <Text size="sm" className="text-red-700 dark:text-red-400">
                              {uploadError}
                            </Text>
                          </div>
                        )}

                        <Button
                          onClick={() => void handleUploadDocumento()}
                          disabled={!selectedFile || uploading}
                          className="w-full !text-white dark:!text-white"
                          style={{ backgroundColor: BRAND_COLOR }}
                        >
                          {uploading ? (
                            <span className="flex items-center justify-center gap-2">
                              <LoadingSpinner size="sm" />
                              Enviando...
                            </span>
                          ) : (
                            'Enviar para Tess'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : currentChat ? (
                  <>
                    {currentChat.file_name && (
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 shrink-0 space-y-1">
                        <Text size="xs" color="muted">
                          Documento:{' '}
                          <span className="text-gray-800 dark:text-gray-200">
                            {currentChat.file_name}
                          </span>
                        </Text>
                        {webhookOffline && (
                          <Text size="xs" className="text-amber-600 dark:text-amber-400">
                            Modo local: webhook n8n indisponível. A Tess ainda não lê o documento;
                            a tela de chat está liberada para visualização.
                          </Text>
                        )}
                      </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 && !messagesLoading && !sending ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center max-w-md">
                            <div
                              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: BRAND_COLOR }}
                            >
                              <SparklesIcon className="h-8 w-8 text-white" />
                            </div>
                            <Heading
                              as="h3"
                              size="2xl"
                              className="text-gray-900 dark:text-white mb-2"
                            >
                              Documento pronto
                            </Heading>
                            <Text size="lg" color="muted" className="mb-4">
                              Faça perguntas sobre o documento. A Tess responderá com base no
                              conteúdo enviado.
                            </Text>
                          </div>
                        </div>
                      ) : (
                        <>
                          {messages.map((message) => (
                            <div key={message.id} className="space-y-4">
                              {message.user_message && (
                                <div className="flex justify-end">
                                  <div
                                    className="max-w-3xl px-4 py-3 rounded-lg"
                                    style={{ backgroundColor: BRAND_COLOR }}
                                  >
                                    <Text
                                      size="sm"
                                      className="whitespace-pre-wrap text-right text-white"
                                    >
                                      {displayUserMessage(message.user_message)}
                                    </Text>
                                    <Text
                                      size="xs"
                                      className="mt-2 text-white text-right opacity-80"
                                    >
                                      {new Date(message.created_at).toLocaleDateString('pt-BR')} às{' '}
                                      {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </Text>
                                  </div>
                                </div>
                              )}

                              {message.ai_message && (
                                <div className="flex justify-start">
                                  <div className="max-w-3xl px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                                    <div className="flex items-start gap-3">
                                      <div
                                        className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-1"
                                        style={{ backgroundColor: BRAND_COLOR }}
                                      >
                                        <SparklesIcon className="h-4 w-4 text-white" />
                                      </div>
                                      <div className="flex-1">
                                        <Text size="sm" className="whitespace-pre-wrap text-left">
                                          {message.ai_message}
                                        </Text>
                                        <Text
                                          size="xs"
                                          className="mt-2 text-gray-500 dark:text-gray-400 text-left"
                                        >
                                          {new Date(message.created_at).toLocaleDateString(
                                            'pt-BR'
                                          )}{' '}
                                          às{' '}
                                          {new Date(message.created_at).toLocaleTimeString(
                                            'pt-BR',
                                            {
                                              hour: '2-digit',
                                              minute: '2-digit',
                                            }
                                          )}
                                        </Text>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {(sending || messagesLoading) && (
                            <div className="flex justify-start">
                              <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-6 h-6 rounded flex items-center justify-center"
                                    style={{ backgroundColor: BRAND_COLOR }}
                                  >
                                    <SparklesIcon className="h-4 w-4 text-white" />
                                  </div>
                                  <div className="flex space-x-1">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                                    <div
                                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                      style={{ animationDelay: '0.1s' }}
                                    />
                                    <div
                                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                      style={{ animationDelay: '0.2s' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {error && (
                      <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
                        <Text size="sm" className="text-red-700 dark:text-red-400">
                          {error}
                        </Text>
                      </div>
                    )}

                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      {!canChat && (
                        <Text size="xs" className="text-amber-600 dark:text-amber-400 mb-2">
                          Este chat ainda não tem documento associado. Inicie uma nova conversa e
                          envie um arquivo.
                        </Text>
                      )}
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <textarea
                            ref={inputRef}
                            placeholder={
                              canChat
                                ? 'Digite sua mensagem...'
                                : 'Envie um documento para habilitar o chat'
                            }
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                if (canChat && !sending) void sendMessage()
                              }
                            }}
                            rows={3}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 resize-none"
                            disabled={sending || !canChat}
                          />
                        </div>
                        <Button
                          onClick={() => void sendMessage()}
                          disabled={!inputMessage.trim() || sending || !canChat}
                          className="flex items-center gap-2 !text-white dark:!text-white"
                          style={{ backgroundColor: BRAND_COLOR }}
                        >
                          <PaperAirplaneIcon className="h-5 w-5" />
                          Enviar
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <Text size="sm" color="muted">
                      Selecione uma conversa ou inicie uma nova.
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>
      </PermissionGuard>
    </div>
  )
}

export default IADocumento
